import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import {
  GET_ALL_DICTIONARIES,
  GET_DICTIONARY_BY_NAME_AND_TYPE,
  CREATE_DICTIONARY,
  UPDATE_DICTIONARY,
} from "redux/dictionaries";
import DictionaryDetail from "components/DictionaryDetail";
import TableDictionary from "components/Tables/TableDictionary";
import SearchIcon from "images/search.svg";
import useDebounce from "../../hooks/useDebounce";
import "./style.scss";

const DictionariesManagement = ({
  getAllDictionaries,
  allDictionaries,
  loading,
  getDictionaryByNameAndType,
  createDictionary,
  updateDictionary,
}) => {
  const [loadingState, setLoadingState] = useState(false);
  const [pageOffset, setPageOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [isOpenDictionaryModal, setIsOpenDictionaryModal] = useState(false);
  const [bulkUploadMode, setBulkUploadMode] = useState(false);
  const [selectedDictionary, setSelectedDictionary] = useState(null);

  const debouncedValue = useDebounce(search, 800);

  const handleChange = event => {
    setSearch(event.target.value);
  };

  const handleViewDetail = async dictionary => {
    try {
      setLoadingState(true);

      // Re-fetch the dictionary details using the service
      const response = await getDictionaryByNameAndType({
        name: dictionary.name,
        type: dictionary.type,
      });

      // The service returns an array, we need to handle different cases
      let dictionaryDetails = null;
      if (response.payload && Array.isArray(response.payload)) {
        if (response.payload.length > 0) {
          // Take the first item if multiple results
          dictionaryDetails = response.payload[0];
        }
      } else if (response.payload) {
        // Handle case where it's not an array
        dictionaryDetails = response.payload;
      }

      if (dictionaryDetails) {
        setSelectedDictionary(dictionaryDetails);
        setIsOpenDictionaryModal(true);
        setBulkUploadMode(false); // Ensure single edit mode
      } else {
        toast.error("Dictionary not found or no longer exists");
      }
    } catch (error) {
      console.error("Error fetching dictionary details:", error);
      toast.error("Error loading dictionary details");
    } finally {
      setLoadingState(false);
    }
  };

  useEffect(() => {
    setLoadingState(true);
    getAllDictionaries({})
      .then(res => {
        setLoadingState(false);
      })
      .catch(e => {
        console.log(e);
        setLoadingState(false);
        toast.error("Error loading dictionaries");
      });
  }, [getAllDictionaries]);

  const handleCreateOrUpdateDictionaries = async dictionariesData => {
    setLoadingState(true);
    let successCount = 0;
    let errorCount = 0;

    for (const dictData of dictionariesData) {
      try {
        // Add null checks to prevent errors
        if (!dictData || !dictData.name || !dictData.type) {
          errorCount++;
          toast.error("Invalid dictionary data: missing name or type");
          continue;
        }

        // Check if the dictionary already exists (for update mode)
        if (selectedDictionary && dictData.id) {
          // Update existing dictionary
          // updateDictionary without passing the id to the body
          const { id, ...updateData } = dictData;
          await updateDictionary(updateData);
          successCount++;
          toast.success(`Dictionary "${dictData.name}" updated successfully`);
        } else {
          // Check if dictionary exists by name and type
          const existingDict = await getDictionaryByNameAndType({
            name: dictData.name,
            type: dictData.type,
          });

          if (existingDict.payload && existingDict.payload.length > 0) {
            // Dictionary exists, update it
            await updateDictionary(dictData);
            successCount++;
            toast.success(`Dictionary "${dictData.name}" updated successfully`);
          } else {
            // Dictionary doesn't exist, create it
            await createDictionary(dictData);
            successCount++;
            toast.success(`Dictionary "${dictData.name}" created successfully`);
          }
        }
      } catch (error) {
        if (error.response?.status === 404) {
          // Dictionary doesn't exist, create it
          try {
            await createDictionary(dictData);
            successCount++;
            toast.success(`Dictionary "${dictData.name}" created successfully`);
          } catch (createError) {
            errorCount++;
            toast.error(
              `Error creating dictionary "${dictData?.name || "unknown"}": ${
                createError.message
              }`
            );
          }
        } else {
          errorCount++;
          toast.error(
            `Error processing dictionary "${dictData?.name || "unknown"}": ${
              error.message
            }`
          );
        }
      }
    }

    // Reload the dictionaries list
    await getAllDictionaries({});

    setLoadingState(false);

    if (successCount > 0 && errorCount === 0) {
      toast.success(
        `All ${successCount} dictionaries were processed successfully`
      );
    } else if (successCount > 0 && errorCount > 0) {
      toast.warning(
        `${successCount} dictionaries processed successfully, ${errorCount} with errors`
      );
    } else if (errorCount > 0) {
      toast.error(`All ${errorCount} dictionaries generated errors`);
    }

    setIsOpenDictionaryModal(false);
  };

  // Filter dictionaries based on search
  const filteredDictionaries = Array.isArray(allDictionaries)
    ? allDictionaries.filter(
        dict =>
          dict.name.toLowerCase().includes(search.toLowerCase()) ||
          dict.type.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  // Calculate total pages for React Table
  const totalPages = Math.ceil(filteredDictionaries.length / pageSize);

  // Reset page when search changes
  useEffect(() => {
    setPageOffset(0);
  }, [debouncedValue]);

  return (
    <div className="container contributor admin">
      <div className="contributor-header ">
        <div className="contributor-banner">
          <span>ADMIN MODE</span>
        </div>
        <div className="title">Dictionaries Management</div>
        <div className="action">
          <div
            className={`add-item`}
            onClick={() => setIsOpenDictionaryModal(true)}>
            ADD NEW DICTIONARY
          </div>
        </div>
        <div className="search">
          <div className="search-bar-container">
            <div className="search-icon">
              <img src={SearchIcon} alt="lens" />
            </div>
            <input
              type="text"
              className="search-bar"
              value={search || ""}
              onChange={handleChange}
              placeholder="Search by name or type..."
            />
          </div>
        </div>
      </div>
      <div className="results-container no-margin">
        <div className="results dictionaries-management-results">
          <TableDictionary
            loading={loadingState || loading}
            data={filteredDictionaries}
            currentPage={pageOffset}
            setCurrentPage={setPageOffset}
            onViewDetail={handleViewDetail}
            pageSize={pageSize}
            totalPages={totalPages}
            setPageSize={setPageSize}
          />
        </div>
      </div>
      <DictionaryDetail
        isOpenProp={isOpenDictionaryModal}
        onClose={() => {
          setIsOpenDictionaryModal(false);
          setSelectedDictionary(null);
        }}
        onSave={handleCreateOrUpdateDictionaries}
        bulkUploadMode={bulkUploadMode}
        setBulkUploadMode={setBulkUploadMode}
        selectedDictionary={selectedDictionary}
      />
    </div>
  );
};

export default connect(
  ({ dictionaries }) => ({
    allDictionaries: dictionaries.allDictionaries,
    loading: dictionaries.loading,
  }),
  dispatch => ({
    getAllDictionaries: params => dispatch(GET_ALL_DICTIONARIES(params)),
    getDictionaryByNameAndType: params =>
      dispatch(GET_DICTIONARY_BY_NAME_AND_TYPE(params)),
    createDictionary: data => dispatch(CREATE_DICTIONARY(data)),
    updateDictionary: data => dispatch(UPDATE_DICTIONARY(data)),
  })
)(DictionariesManagement);
